// File: src/components/common/UnsavedChangesDialog.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Confirmation dialog for handling unsaved changes when navigating away.
// Provides three options: Stay, Discard changes, or Save and continue.

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

/**
 * UnsavedChangesDialog - Confirmation dialog for unsaved changes
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onDiscard - Callback to discard changes and continue
 * @param {function} props.onSaveAndContinue - Callback to save changes and continue
 * @param {function} props.onCancel - Callback to cancel and stay on current view
 * @param {boolean} [props.isSaving] - Whether save operation is in progress
 * @param {string} [props.action] - Description of the pending action (e.g., "switch tabs", "navigate away")
 */
const UnsavedChangesDialog = ({
    open,
    onDiscard,
    onSaveAndContinue,
    onCancel,
    isSaving = false,
    action = 'continue',
}) => {
    return (
        <Dialog
            open={open}
            onClose={!isSaving ? onCancel : undefined}
            maxWidth="sm"
            fullWidth
            data-testid="dialog-unsaved-changes"
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                Unsaved Changes
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You have unsaved changes. Would you like to save them before you {action}?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onCancel}
                    disabled={isSaving}
                    data-testid="dialog-unsaved-btn-stay"
                >
                    Stay
                </Button>
                <Button
                    onClick={onDiscard}
                    color="error"
                    disabled={isSaving}
                    data-testid="dialog-unsaved-btn-discard"
                >
                    Discard
                </Button>
                <Button
                    onClick={onSaveAndContinue}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    data-testid="dialog-unsaved-btn-save"
                >
                    {isSaving ? 'Saving...' : 'Save & Continue'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnsavedChangesDialog;
